import {
  AlertTriangle,
  Bell,
  Download,
  Ghost,
  Package,
  Settings,
  Shield,
  ShieldCheck,
  ShieldPlus,
  type LucideIcon,
} from 'lucide-react';
import { type ComponentType } from 'react';
import { PostureManagement } from '@/components/Inventory';
import InstallTab from '@/components/InstallTab/InstallTab';
import AttackDemonstrationsTab from '@/components/AttackDemonstrationsTab';
import { LlmVulnDiscoveryPage } from '@/components/LlmVulnDiscovery/LlmVulnDiscoveryPage';
import AgentsActivityTab from '../organization/tabs/AgentsActivityTab';
import { UserTab, type UserTabType } from './constants';
import GroupAlertsTab from './tabs/GroupAlertsTab';
import PoliciesTab from './tabs/PoliciesTab';
import SecureAiCodingTab from './tabs/SecureAiCodingTab';
import SettingsTab from './tabs/SettingsTab';
import ShadowAITab from './tabs/ShadowAITab';
import ThreatModelingTab from './tabs/ThreatModelingTab';

/**
 * Tab configuration types
 */
export type TabComponentType = ComponentType<unknown>;
export type UserTabConfig = Record<
  UserTabType,
  {
    label: string;
    icon: LucideIcon;
    component: TabComponentType;
  }
>;

/**
 * Tab configuration: labels, icons, and components
 *
 * KIR-4480: key insertion order IS the sidebar order — `Dashboard` renders
 * `Object.keys(tabConfig)`. `Install` ("Get Started") leads only until the
 * account has an API key; `useReorderTabsOnSetup` then moves it after
 * `AttackDemonstrations`. Mirrors the organization sidebar order for the tabs
 * a personal account has (see `pages/organization/constants.ts`).
 */
export const TAB_CONFIG: UserTabConfig = {
  [UserTab.Install]: {
    label: 'Get Started',
    icon: Download,
    component: InstallTab as TabComponentType,
  },
  [UserTab.PostureManagement]: {
    label: 'Inventory',
    icon: ShieldCheck,
    component: PostureManagement as TabComponentType,
  },
  [UserTab.Alerts]: {
    label: 'Alerts',
    icon: Bell,
    component: GroupAlertsTab,
  },
  [UserTab.ShadowAI]: {
    label: 'Shadow AI',
    icon: Ghost,
    component: ShadowAITab,
  },
  [UserTab.AgentsActivity]: {
    label: 'Packages & Repos',
    icon: Package,
    component: AgentsActivityTab as TabComponentType,
  },
  [UserTab.Policies]: {
    label: 'Policies',
    icon: Shield,
    component: PoliciesTab,
  },
  [UserTab.SecureAiCoding]: {
    label: 'Secure AI Coding Enforcement',
    icon: ShieldPlus,
    component: SecureAiCodingTab,
  },
  [UserTab.ThreatModeling]: {
    label: 'Threat Modeling',
    icon: ShieldPlus,
    component: ThreatModelingTab,
  },
  [UserTab.LlmVulnDiscovery]: {
    label: 'Vulnerability Discovery with LLM',
    icon: ShieldPlus,
    component: LlmVulnDiscoveryPage,
  },
  [UserTab.Settings]: {
    label: 'Settings',
    icon: Settings,
    component: SettingsTab,
  },
  [UserTab.AttackDemonstrations]: {
    label: 'Attack Demonstrations',
    icon: AlertTriangle,
    component: AttackDemonstrationsTab,
  },
};
